/** ***********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ************************************************************************* */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import uuid from 'uuid';
import Header from '@ccxi/ccxi-shared-components/src/Header';
import { FrameSection, FrameItem } from '@ccxi/ccxi-shared-components/src/HeaderFooterBase';
import Button from '@react/react-spectrum/Button';
import Tooltip from '@react/react-spectrum/Tooltip';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Icons from '../../../../utils/iconMapping';
import './pdpHeader.scss';
import AppActionBar from './appActionBar';
import { PDP_SECTION, PDP_ACTION, onBackClick } from '../../../../utils/pdpUtils/pdpEnums';
import TranslationUtil from '../../../../utils/translation';


class PDPHeader extends Component {
    onBackButtonClick(e) {
        const { headerData: { actionHandlerCallback } } = this.props;
        onBackClick(e, actionHandlerCallback);
    }

    onProductHeadingClick(e) {
        const { headerData: { actionHandlerCallback } } = this.props;
        const guid = uuid.v4();
        const actionParam = {
            actionObject: {
                sectionId: PDP_SECTION.HEADER,
	            actionId: PDP_ACTION.HEADING_CLICK,
            },
            guid,
        };
        actionHandlerCallback(
            e, PDP_SECTION.HEADER, PDP_ACTION.HEADING_CLICK, actionParam
        );
    }

    get headerData() {
        const { headerData: { app } } = this.props;
        return app;
    }

    renderLeftSection() {
        const LeftIcon = Icons.ArrowLeft;
        const { appFamilyName, platform, iconURL } = this.headerData;
        /* eslint-disable jsx-a11y/anchor-is-valid */
        return (
            <FrameSection position="left" className="left-section">
                <FrameItem
                    onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                            this.onBackButtonClick(e);
                        }
                    }}
                    onClick={(e) => { this.onBackButtonClick(e); }}
                    className="back-icon">
                    <OverlayTrigger placement="bottom" trigger={['hover']} delayShow={1000}>
                        <Button variant="action" icon={<LeftIcon size="XS" />} quiet aria-label="Back" />
                        <Tooltip>
                            {TranslationUtil.getMessage('ccx.pdp.back.tooltip')}
                        </Tooltip>
                    </OverlayTrigger>
                </FrameItem>
                <FrameItem
                    className="product-heading"
                    role="heading"
                    aria-level="1"
                    aria-label={`${appFamilyName} ${platform}`}>
                    <div className="product-heading-wrapper">
                        <div>
                            <img alt={appFamilyName} className="product-icon" src={iconURL} />
                        </div>
                        <div className="product-title">
                            <div className="product-name">{appFamilyName}</div>
                            <div className="product-platform">
                                {TranslationUtil.getMessage(`ccx.app.platform.${this.headerData.platform}`)}
                            </div>
                        </div>
                    </div>
                </FrameItem>
            </FrameSection>
        );
        /* eslint-enable jsx-a11y/anchor-is-valid */
    }

    render() {
        const {
            headerData: {
                app, applicableActionList,
            },
        } = this.props;
        // NOTE: update element classname in new-relic also (if applicable)
        return (
            <Header className="pdp-header">
                {this.renderLeftSection()}
                <FrameSection position="right" className="right-section">
                    <FrameItem>
                        <AppActionBar
                            app={app}
                            applicableActionList={applicableActionList}
                            source="pdp" />
                    </FrameItem>
                </FrameSection>
            </Header>
        );
    }
}

export default observer(PDPHeader);
